import React, { useState, useEffect } from 'react';
import {
  VStack, SimpleGrid, FormControl, FormLabel, Input, Select, Textarea,
  Button, HStack, Badge, Table, Thead, Tbody, Tr, Th, Td, Text, Box, Spinner,
  useToast,
} from '@chakra-ui/react';
import {
  ImmCaso, ImmCasoHistorial, ImmTipoViolencia, ImmArchivo,
  crearCaso, actualizarCaso, obtenerCaso, obtenerHistorialCaso, listarTiposViolencia,
} from '../../../api/immApi';
import {
  SectionTitle, Field, ExpedienteSelect, IMM_COLOR, IMM_COLOR_HOVER,
  fmt, fmtDateTime, NIVEL_RIESGO_COLOR, GaleriaEvidencias,
} from '../_shared';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: ImmCaso;
  onGuardado?: (c: ImmCaso) => void;
  onCancelar?: () => void;
}

const NIVELES_RIESGO = ['Bajo', 'Medio', 'Alto'];
const ESTATUS = ['Abierto', 'EnSeguimiento', 'Cerrado', 'Anulado'];
const hoy = () => new Date().toISOString().split('T')[0];

const FormularioCaso: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const [guardando, setGuardando] = useState(false);
  const [expedienteId, setExpedienteId] = useState<number | undefined>(data?.expedienteId);
  const [tipos, setTipos] = useState<ImmTipoViolencia[]>([]);
  const [historial, setHistorial] = useState<ImmCasoHistorial[]>([]);
  const [cargandoHist, setCargandoHist] = useState(false);
  const [archivos, setArchivos] = useState<ImmArchivo[]>(data?.archivos || []);

  const [f, setF] = useState<Partial<ImmCaso>>({
    fechaIncidente: data?.fechaIncidente ? data.fechaIncidente.split('T')[0] : hoy(),
    tipoViolenciaId: data?.tipoViolenciaId ?? undefined,
    nivelRiesgo: data?.nivelRiesgo || '',
    descripcion: data?.descripcion || '',
    medidasProteccion: data?.medidasProteccion || '',
    acciones: data?.acciones || '',
    dependenciasInvolucradas: data?.dependenciasInvolucradas || '',
    observaciones: data?.observaciones || '',
    estatus: data?.estatus || 'Abierto',
  });

  useEffect(() => {
    listarTiposViolencia().then(setTipos).catch(() => setTipos([]));
  }, []);

  useEffect(() => {
    if (modo === 'view' && data) {
      setCargandoHist(true);
      obtenerHistorialCaso(data.id)
        .then(setHistorial)
        .catch(() => setHistorial([]))
        .finally(() => setCargandoHist(false));
      // Cargar el caso completo para obtener sus evidencias/archivos.
      obtenerCaso(data.id)
        .then((c) => setArchivos(c.archivos || []))
        .catch(() => { /* mantiene los que ya trae data */ });
    }
  }, [modo, data]);

  const set = (campo: keyof ImmCaso, valor: any) => setF((prev) => ({ ...prev, [campo]: valor }));

  const handleGuardar = async () => {
    if (!expedienteId) {
      toast({ title: 'Selecciona un expediente', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const payload: Partial<ImmCaso> = {
        ...f,
        expedienteId,
        tipoViolenciaId: f.tipoViolenciaId != null && String(f.tipoViolenciaId) !== '' ? Number(f.tipoViolenciaId) : undefined,
      };
      const res = modo === 'edit' && data
        ? await actualizarCaso(data.id, payload)
        : await crearCaso(payload);
      toast({ title: modo === 'edit' ? 'Caso actualizado' : 'Caso registrado', status: 'success', duration: 3000 });
      onGuardado?.(res);
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo guardar el caso.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  if (modo === 'view' && data) {
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Datos del caso</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Field label="Expediente" value={data.expediente?.folio ?? ('#' + data.expedienteId)} />
          <Field label="Fecha del incidente" value={fmt(data.fechaIncidente)} />
          <Field label="Tipo de violencia" value={data.tipoViolencia?.descripcion} />
          <Field
            label="Nivel de riesgo"
            value={<Badge colorScheme={NIVEL_RIESGO_COLOR[data.nivelRiesgo] || 'gray'}>{data.nivelRiesgo || '—'}</Badge>}
          />
          <Field label="Estatus" value={data.estatus} />
        </SimpleGrid>

        <SectionTitle>Detalle</SectionTitle>
        <SimpleGrid columns={{ base: 1 }} spacing={4}>
          <Field label="Descripción" value={data.descripcion} />
          <Field label="Medidas de protección" value={data.medidasProteccion} />
          <Field label="Acciones" value={data.acciones} />
          <Field label="Dependencias involucradas" value={data.dependenciasInvolucradas} />
          <Field label="Observaciones" value={data.observaciones} />
        </SimpleGrid>

        <SectionTitle>Evidencias / documentos</SectionTitle>
        <GaleriaEvidencias archivos={archivos} />

        <SectionTitle>Historial de cambios</SectionTitle>
        {cargandoHist ? (
          <HStack py={3}><Spinner size="sm" color={IMM_COLOR} /><Text fontSize="sm" color="gray.500">Cargando historial...</Text></HStack>
        ) : historial.length === 0 ? (
          <Text fontSize="sm" color="gray.400" py={2}>Sin movimientos registrados.</Text>
        ) : (
          <Box overflowX="auto" borderRadius="md" borderWidth="1px">
            <Table size="sm" variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th fontSize="xs">Fecha</Th>
                  <Th fontSize="xs">Usuario</Th>
                  <Th fontSize="xs">Acción</Th>
                </Tr>
              </Thead>
              <Tbody>
                {historial
                  .slice()
                  .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime())
                  .map((h) => (
                    <Tr key={h.id}>
                      <Td fontSize="xs">{fmtDateTime(h.fecha)}</Td>
                      <Td fontSize="xs">{h.usuario || '—'}</Td>
                      <Td fontSize="xs">{h.accion}</Td>
                    </Tr>
                  ))}
              </Tbody>
            </Table>
          </Box>
        )}

        <HStack justify="flex-end" pt={2}>
          <Button variant="outline" onClick={onCancelar}>Cerrar</Button>
        </HStack>
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Datos del caso</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Expediente</FormLabel>
          <ExpedienteSelect value={expedienteId} onChange={setExpedienteId} isDisabled={modo !== 'new'} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha del incidente</FormLabel>
          <Input type="date" value={(f.fechaIncidente as string) || ''} onChange={(e) => set('fechaIncidente', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Tipo de violencia</FormLabel>
          <Select placeholder="Selecciona" value={f.tipoViolenciaId ?? ''} onChange={(e) => set('tipoViolenciaId', e.target.value ? Number(e.target.value) : undefined)}>
            {tipos.map((t) => <option key={t.id} value={t.id}>{t.descripcion}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Nivel de riesgo</FormLabel>
          <Select placeholder="Selecciona" value={f.nivelRiesgo || ''} onChange={(e) => set('nivelRiesgo', e.target.value)}>
            {NIVELES_RIESGO.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={f.estatus || 'Abierto'} onChange={(e) => set('estatus', e.target.value)}>
            {ESTATUS.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Detalle</SectionTitle>
      <SimpleGrid columns={{ base: 1 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Descripción</FormLabel>
          <Textarea rows={3} value={f.descripcion || ''} onChange={(e) => set('descripcion', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Medidas de protección</FormLabel>
          <Textarea rows={2} value={f.medidasProteccion || ''} onChange={(e) => set('medidasProteccion', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Acciones</FormLabel>
          <Textarea rows={2} value={f.acciones || ''} onChange={(e) => set('acciones', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Dependencias involucradas</FormLabel>
          <Textarea rows={2} value={f.dependenciasInvolucradas || ''} onChange={(e) => set('dependenciasInvolucradas', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Observaciones</FormLabel>
          <Textarea rows={2} value={f.observaciones || ''} onChange={(e) => set('observaciones', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
          Guardar caso
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioCaso;
